import type { TFunction } from "i18next";

import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import Shepherd from "shepherd.js";

import ButtonIcon from "../../images/rebrandIcons/ButtonIcon.png";

function createTourButtonHTML(label: string, direction: "left" | "right" | null = null, t: TFunction): string {
  const text: string = t(label, label);
  const arrowLeft = `<img class="h-2.5 mr-3 mb-1 transform rotate-180" src="${ButtonIcon}" alt="${text} Arrow Icon" />`;
  const arrowRight = `<img class="h-2.5 ml-3 mb-1" src="${ButtonIcon}" alt="${text} Arrow Icon" />`;
  return `
    <div class="flex gap-1 items-center">
      ${direction === "left" ? arrowLeft : ""}
      <p>${text}</p>
      ${direction === "right" ? arrowRight : ""}
    </div>
  `;
}

function useGigsClaimTour() {
  const { t } = useTranslation(["tutorial", "onboarding", "common", "writers"]);

  const startTour = useCallback((manual: boolean = false) => {
    const tour = new Shepherd.Tour({
      useModalOverlay: true,
      defaultStepOptions: {
        classes: "bg-white font-sentric rounded-[10px] text-gray-800 shadow-xl p-6",
        cancelIcon: { enabled: true },
        scrollTo: { behavior: "smooth", block: "center" },
      },
    });

    if (!manual) {
      tour.on("cancel", () => localStorage.setItem("gigsClaimTourCompleted", "true"));
    }
    tour.on("complete", () => localStorage.setItem("gigsClaimTourCompleted", "true"));

    const textCountrySelection: string = t("tutorial:gigs-claim-country-selection", "Select the country where the performance took place. This helps correctly locate and validate the venue.");
    const textRestOfForm: string = t("tutorial:gigs-claim-rest-of-form", "Enter the information for the gig you are submitting as a live claim. Please be aware that some PROs may require different details for a successful claim, so make sure to include as much information about your gig as possible.");
    const textButtons: string = t("tutorial:gigs-claim-buttons", "When the form is completed, click to add or update your claim.");

    // Step 1 — country select (only exists if adminRights is true in your form)
    tour.addStep({
      id: "gigsClaimCountrySelectionTour",
      text: textCountrySelection,
      attachTo: { element: "#btnGigsClaimCountry", on: "right" },
      modalOverlayOpeningPadding: 10,
      modalOverlayOpeningRadius: 8,
      classes: "ml-2",
      showOn: () => !!document.querySelector("#btnGigsClaimCountry"),
      buttons: [
        { text: createTourButtonHTML("writers:close", null, t), action: () => tour.next() },
      ],
    });

    tour.addStep({
      id: "gigsClaimRestOfFormTour",
      text: textRestOfForm,
      beforeShowPromise: async () => waitForSelector("#btnGigsClaimVenue"),
      buttons: [
        { text: createTourButtonHTML("common:next-button", "right", t), action: () => tour.next() },
      ],
    });

    tour.addStep({
      id: "setlistNotesSectionTour",
      text: textButtons,
      attachTo: { element: "#gigsClaimButtons", on: "right" },
      classes: "ml-2",
      modalOverlayOpeningPadding: 10,
      modalOverlayOpeningRadius: 8,
      beforeShowPromise: async () => waitForSelector("#gigsClaimButtons"),
      buttons: [
        { text: createTourButtonHTML("onboarding:back-button", "left", t), action: () => tour.back() },
        {
          text: createTourButtonHTML("onboarding:complete-button", null, t),
          action: () => {
            localStorage.setItem("gigsClaimTourCompleted", "true");
            tour.complete();
          },
        },
      ],
    });

    void tour.start();
  }, [t]);

  return startTour;
}

export default useGigsClaimTour;

// Utility: wait until a DOM element exists or timeout
async function waitForSelector(selector: string, { timeout = 8000 }: { timeout?: number } = {}): Promise<Element> {
  return new Promise((resolve, reject) => {
    const found = document.querySelector(selector);
    if (found)
      return resolve(found);

    const observer = new MutationObserver(() => {
      const el = document.querySelector(selector);
      if (el) {
        observer.disconnect();
        resolve(el);
      }
    });

    observer.observe(document.documentElement, { childList: true, subtree: true });

    setTimeout(() => {
      observer.disconnect();
      reject(new Error(`Timed out waiting for ${selector}`));
    }, timeout);
  });
}
